import { Grid, Box, Stack, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { BiFile } from 'react-icons/bi';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { MdOutlineAddLocationAlt } from 'react-icons/md';
import {
  WsFilterDialog,
  WsColors,
  WsLocationSearch,
  WsDateTimePicker,
  WsButton,
  WsDatePicker,
  WsSelect,
  WsSelectObj,
  WsElementWithPopOver,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { WsSearchFilter } from 'wisestep-website/src/components';

import { consultantVisaOptions } from '../../helper/consultantVisaOptions';

interface IActiveConsultantsFilter {
  setOpenAdvancedFilters: Dispatch<SetStateAction<boolean>>;
  performSearch: (data) => void;
  onReset: () => void;
}

const ActiveConsultantsSearchFilter = ({
  setOpenAdvancedFilters,
  performSearch,
  onReset,
}: IActiveConsultantsFilter) => {
  const [isDateTime, setDateTime] = useState<boolean>(false);
  const methods = useFormContext();
  const { handleSubmit, watch, setValue } = methods;
  const locationPreference: WsSelectObj[] = [
    {
      label: 'Current',
      value: 'Current',
    },
    {
      label: 'Preferred',
      value: 'Preferred',
    },
    {
      label: 'Current or Preferred',
      value: 'Current or Preferred',
    },
  ];

  return (
    <FormProvider {...methods}>
      <Grid container>
        <Grid item xs={12}>
          <Box component="form" onSubmit={handleSubmit(performSearch)}>
            <WsSearchFilter
              name="consultantFilter"
              showDefaultRightActions={true}
              showLeftActions={true}
              showChildren={false}
              onClickFilter={() => setOpenAdvancedFilters(true)}
              clearAllFields={() => onReset()}
              leftActionsChildren={
                <Grid container pt={1}>
                  <Grid item xs={12} pt={2}>
                    <WsLocationSearch
                      distanceSelectName="Radius"
                      placeHolder="Search For Locations"
                      size="small"
                      showDistance={true}
                      name="locations"
                      multiSelect={true}
                      wisestepApi={true}
                    />
                  </Grid>
                  <Grid item xs={4} pt={2}>
                    <WsFilterDialog
                      name="Location Preference"
                      icon={<MdOutlineAddLocationAlt size={25} color={WsColors.TheBlues500} />}
                      label="Location"
                    >
                      <Box p={2} minWidth={450}>
                        <WsSelect name="locationPreference" selectArray={locationPreference} />
                      </Box>
                    </WsFilterDialog>
                  </Grid>
                  <Grid item xs={4} pt={2}>
                    <WsFilterDialog
                      name="Last Active Between Dates"
                      icon={<FaRegCalendarAlt size={21} color={WsColors.TheBlues500} />}
                      label="Last Active"
                    >
                      <Box p={1}>
                        {!isDateTime && (
                          <Stack spacing={2} direction="row" pt={3} pl={1}>
                            <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
                            <WsDatePicker name="toDate" label="To Date" disableFuture={true} />
                          </Stack>
                        )}
                        {isDateTime && (
                          <Stack spacing={2} direction="row" pt={3} pl={1}>
                            <WsDateTimePicker
                              name="fromDate"
                              label="From Date"
                              disableFuture={true}
                            />
                            <WsDateTimePicker name="toDate" label="To Date" disableFuture={true} />
                          </Stack>
                        )}
                        {!isDateTime && (
                          <Box px={1}>
                            <WsButton variant="text" size="small" onClick={() => setDateTime(true)}>
                              select Date And Time
                            </WsButton>
                          </Box>
                        )}
                        {isDateTime && (
                          <Box px={1}>
                            <WsButton
                              variant="text"
                              size="small"
                              onClick={() => setDateTime(false)}
                            >
                              select Date Only
                            </WsButton>
                          </Box>
                        )}
                      </Box>
                    </WsFilterDialog>
                  </Grid>
                  <Grid item container xs={4} pt={2}>
                    <Grid item pt={1} xs={2}>
                      <BiFile size={25} color={WsColors.TheBlues500} />
                    </Grid>
                    <Grid item xs={10}>
                      <Typography
                        color={WsColors.TertiaryShades600}
                        fontSize="10px"
                        fontWeight="bold"
                        sx={{ display: 'flex' }}
                      >
                        Visa Status
                      </Typography>
                      <Box>
                        <WsElementWithPopOver
                          element={
                            <WsTypography variant={WsTypoVariants.BODY2} cursor="pointer">
                              {watch('visaStatus') ?? 'Visa Status'}
                            </WsTypography>
                          }
                        >
                          <Grid rowGap={3}>
                            {consultantVisaOptions.map((e, index) => (
                              <Box
                                key={index}
                                p={1}
                                sx={{
                                  '&:hover': {
                                    background: WsColors.TheBlues500,
                                  },
                                }}
                              >
                                <WsTypography
                                  onClick={() => setValue('visaStatus', e.value)}
                                  variant={WsTypoVariants.SUB_TITLE}
                                  cursor="pointer"
                                >
                                  {e.label}
                                </WsTypography>
                              </Box>
                            ))}
                          </Grid>
                        </WsElementWithPopOver>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              }
            />
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default ActiveConsultantsSearchFilter;
